// src/pages/AdminDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import "./AdminDashboard.css";

// Backend base URL and admin key from environment
import API_BASE from "../api/apiBase";
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_KEY;

// ---------- small helpers ----------
const toFiniteNumber = (v) => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

const safeLower = (v) => String(v || "").toLowerCase();

const getTimestampMillis = (ts) => {
  if (!ts) return 0;

  // Firestore Timestamp
  if (typeof ts?.toDate === "function") {
    try {
      return ts.toDate().getTime();
    } catch {
      return 0;
    }
  }

  // Timestamp-like object
  if (typeof ts === "object") {
    if (typeof ts._seconds === "number") return ts._seconds * 1000;
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
  }

  const n = new Date(ts).getTime();
  return Number.isNaN(n) ? 0 : n;
};

const formatDate = (ts) => {
  const ms = getTimestampMillis(ts);
  return ms ? new Date(ms).toLocaleString() : "Unknown";
};

function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    type: "all", // all | surface | room | multiroom | legacy
    name: "",
    email: "",
    phone: "",
    minWidth: "",
    maxWidth: "",
    minLength: "",
    maxLength: "",
    minArea: "",
    maxArea: "",
    minConfidence: "",
    dateFrom: "",
    dateTo: "",
    userId: "",
  });

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc",
  });

  // ---------------- API CALLS ----------------
  async function loadProjects() {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/projects`, {
        headers: { "x-admin-key": ADMIN_API_KEY },
      });

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const data = await res.json();
      if (!data.ok) throw new Error(data.message || "Failed to load projects");

      setProjects(Array.isArray(data.projects) ? data.projects : []);
    } catch (err) {
      console.error("AdminDashboard fetch error:", err);
      setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }

  async function deleteProject(id) {
    if (!window.confirm("Delete this project permanently?")) return;

    try {
      const res = await fetch(`${API_BASE}/api/deleteProject/${id}`, {
        method: "DELETE",
        headers: { "x-admin-key": ADMIN_API_KEY },
      });

      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || "Delete failed");

      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      alert("Error deleting project: " + (err.message || "Delete failed"));
    }
  }

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- Normalization + computed fields ----------------
  const normalizedTimestamp = (p) => p?.timestamp || p?.createdAt || p?.sharedAt || null;

  const normalizedEmail = (p) => p?.email || p?.ownerEmail || p?.sharedByEmail || "—";
  const normalizedUserId = (p) => p?.userId || p?.ownerUid || p?.ownerUID || "—";
  const normalizedPhone = (p) => p?.phone || "—";

  const getProjectType = (p) => {
    // legacy: old room records without projectName
    const maybeLegacy = (p?.projectType || "").toLowerCase() === "room" && !p?.projectName;
    if (maybeLegacy) return "legacy";

    const t = (p?.projectType || p?.mode || "").toLowerCase();
    if (t === "surface") return "surface";
    if (t === "multiroom") return "multiroom";
    if (t === "room") return "room";

    // infer:
    if (Array.isArray(p?.rooms)) return "multiroom";
    if (p?.surfaceUrl) return "surface";

    return "room";
  };

  const typeLabel = (p) => {
    const t = getProjectType(p);
    if (t === "legacy") return "Legacy";
    if (t === "surface") return "Surface";
    if (t === "multiroom") return "MultiRoom";
    return "Room";
  };

  const nameLabel = (p) => {
    const t = getProjectType(p);
    if (t === "legacy") return "Legacy";
    return (
      p?.projectName ||
      p?.surfaceName ||
      p?.roomName ||
      (typeof p?.id === "string" ? `Project ${p.id.slice(-8)}` : "—")
    );
  };

  const getSingleRoom = (p) => {
    const rooms = Array.isArray(p?.rooms) ? p.rooms : [];
    return rooms.length === 1 ? rooms[0] : null;
  };

  const numericWidth = (p) => {
    const t = getProjectType(p);

    // MultiRoom: only show width if exactly 1 room (otherwise misleading)
    if (t === "multiroom") {
      const r = getSingleRoom(p);
      if (!r) return null;

      const candidates = [
        r?.aiWidthFt,
        r?.aiWidth, // your Firestore uses aiWidth
        r?.widthFt,
        r?.tmWidthFt,
        r?.tapWidthFt,
      ];
      for (const v of candidates) {
        const n = toFiniteNumber(v);
        if (n != null) return n;
      }
      return null;
    }

    // Room/Surface: prefer AI
    const candidates = [p?.aiWidthFt, p?.aiWidth, p?.widthFt, p?.tmWidthFt, p?.tapWidthFt];
    for (const v of candidates) {
      const n = toFiniteNumber(v);
      if (n != null) return n;
    }
    return null;
  };

  const numericLength = (p) => {
    const t = getProjectType(p);

    if (t === "multiroom") {
      const r = getSingleRoom(p);
      if (!r) return null;

      const candidates = [
        r?.aiLengthFt,
        r?.aiLength, // your Firestore uses aiLength
        r?.lengthFt,
        r?.tmLengthFt,
        r?.tapLengthFt,
      ];
      for (const v of candidates) {
        const n = toFiniteNumber(v);
        if (n != null) return n;
      }
      return null;
    }

    const candidates = [p?.aiLengthFt, p?.aiLength, p?.lengthFt, p?.tmLengthFt, p?.tapLengthFt];
    for (const v of candidates) {
      const n = toFiniteNumber(v);
      if (n != null) return n;
    }
    return null;
  };

  const numericArea = (p) => {
    const t = getProjectType(p);

    if (t === "multiroom") {
      // 1) preferred: summary.totalAiArea
      const fromSummary = toFiniteNumber(p?.summary?.totalAiArea);
      if (fromSummary != null) return fromSummary;

      // 2) fallback: sum rooms[].aiArea
      if (Array.isArray(p?.rooms) && p.rooms.length) {
        const sum = p.rooms.reduce((acc, r) => {
          const a =
            toFiniteNumber(r?.aiArea) ??
            toFiniteNumber(r?.areaSqFt) ??
            (() => {
              const w = toFiniteNumber(r?.aiWidth) ?? toFiniteNumber(r?.aiWidthFt);
              const l = toFiniteNumber(r?.aiLength) ?? toFiniteNumber(r?.aiLengthFt);
              if (w != null && l != null) return w * l;
              return null;
            })();

          return acc + (a ?? 0);
        }, 0);

        return sum > 0 ? sum : null;
      }

      // 3) optional fallback
      const topLevel = toFiniteNumber(p?.totalAreaSqFt);
      if (topLevel != null) return topLevel;

      return null;
    }

    // Surface/Room
    const area = toFiniteNumber(p?.areaSqFt);
    if (area != null) return area;

    const w = numericWidth(p);
    const l = numericLength(p);
    if (w != null && l != null) return w * l;

    return null;
  };

  const numericConfidencePct = (p) => {
    const t = getProjectType(p);

    if (t === "multiroom") {
      const fromSummary = toFiniteNumber(p?.summary?.avgConfidence);
      if (fromSummary != null) return Math.round(fromSummary);

      if (Array.isArray(p?.rooms) && p.rooms.length) {
        const vals = p.rooms
          .map((r) => toFiniteNumber(r?.confidencePct))
          .filter((n) => n != null);

        if (vals.length) {
          const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
          return Math.round(avg);
        }
      }
      return null;
    }

    // Room/Surface
    const aiC = toFiniteNumber(p?.aiConfidence);
    if (aiC != null) return Math.round(aiC <= 1 ? aiC * 100 : aiC);

    const c = toFiniteNumber(p?.confidence);
    if (c != null) return Math.round(c <= 1 ? c * 100 : c);

    return null;
  };

  // ---------------- Filtering + Sorting ----------------
  const filteredAndSortedProjects = useMemo(() => {
    let list = [...projects];

    // filtering
    list = list.filter((p) => {
      const t = getProjectType(p);

      if (filters.type !== "all" && t !== filters.type) return false;

      if (filters.name && !safeLower(nameLabel(p)).includes(safeLower(filters.name))) return false;
      if (filters.email && !safeLower(normalizedEmail(p)).includes(safeLower(filters.email))) return false;
      if (filters.phone && !safeLower(normalizedPhone(p)).includes(safeLower(filters.phone))) return false;
      if (filters.userId && !safeLower(normalizedUserId(p)).includes(safeLower(filters.userId))) return false;

      const w = numericWidth(p);
      if (filters.minWidth && w != null && w < parseFloat(filters.minWidth)) return false;
      if (filters.maxWidth && w != null && w > parseFloat(filters.maxWidth)) return false;

      const l = numericLength(p);
      if (filters.minLength && l != null && l < parseFloat(filters.minLength)) return false;
      if (filters.maxLength && l != null && l > parseFloat(filters.maxLength)) return false;

      const a = numericArea(p);
      if (filters.minArea && a != null && a < parseFloat(filters.minArea)) return false;
      if (filters.maxArea && a != null && a > parseFloat(filters.maxArea)) return false;

      const c = numericConfidencePct(p);
      if (filters.minConfidence && c != null && c < parseFloat(filters.minConfidence)) return false;

      const tsMillis = getTimestampMillis(normalizedTimestamp(p));
      if (filters.dateFrom) {
        const from = new Date(filters.dateFrom + "T00:00:00").getTime();
        if (tsMillis < from) return false;
      }
      if (filters.dateTo) {
        const to = new Date(filters.dateTo + "T23:59:59").getTime();
        if (tsMillis > to) return false;
      }

      return true;
    });

    // sorting (timestamp default)
    list.sort((a, b) => {
      const dir = sortConfig.direction === "asc" ? 1 : -1;

      const av = getTimestampMillis(normalizedTimestamp(a));
      const bv = getTimestampMillis(normalizedTimestamp(b));

      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    return list;
  }, [projects, filters, sortConfig]);

  // ---------------- Render ----------------
  if (loading) return <div className="admin-loading">Loading…</div>;

  if (error) {
    return (
      <div className="admin-container">
        <h1>Admin Dashboard</h1>
        <p style={{ color: "red" }}>Error loading projects: {error}</p>
        <button className="retry-btn" onClick={loadProjects}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div>
          <h1>Admin Project Dashboard</h1>
          <p>Monitor all homeowner submissions across MeasureSmart AI .</p>
        </div>
        <div className="admin-header-meta">
          <div>
            Total projects: <strong>{projects.length}</strong>
          </div>
          <div>
            Showing: <strong>{filteredAndSortedProjects.length}</strong>
          </div>
        </div>
      </header>

      <div className="admin-project-list">
        <div className="table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Homeowner</th>
                <th>User ID</th>
                <th>Phone</th>
                <th>Width</th>
                <th>Length</th>
                <th>Area</th>
                <th>Confidence</th>
                <th>Date</th>
                <th>Delete</th>
              </tr>
            </thead>

            <tbody>
              {filteredAndSortedProjects.map((p) => {
                const t = getProjectType(p);
                const isLegacy = t === "legacy";

                return (
                  <tr key={p.id}>
                    <td>
                      <span
                        className={`project-type-chip ${
                          isLegacy
                            ? "project-type-legacy"
                            : t === "surface"
                            ? "project-type-surface"
                            : t === "multiroom"
                            ? "project-type-multiroom"
                            : "project-type-room"
                        }`}
                      >
                        {typeLabel(p)}
                      </span>
                    </td>

                    <td>{nameLabel(p)}</td>
                    <td>{normalizedEmail(p)}</td>
                    <td>{normalizedUserId(p)}</td>
                    <td>{normalizedPhone(p)}</td>
                    <td>{numericWidth(p)?.toFixed(2) ?? "—"}</td>
                    <td>{numericLength(p)?.toFixed(2) ?? "—"}</td>
                    <td>{numericArea(p)?.toFixed(2) ?? "—"}</td>
                    <td>
                      {numericConfidencePct(p) !== null ? `${numericConfidencePct(p)}%` : "—"}
                    </td>
                    <td>{formatDate(normalizedTimestamp(p))}</td>

                    <td>
                      {isLegacy ? (
                        <span style={{ color: "#999", fontStyle: "italic" }}>—</span>
                      ) : (
                        <button className="admin-delete-btn" onClick={() => deleteProject(p.id)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {filteredAndSortedProjects.length === 0 && (
                <tr>
                  <td colSpan={11} className="no-results-row">
                    No projects match your current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
